import type {
  AmazonPaySignaturePayload,
  OverduePaymentPayload,
  PaymentPayload,
  UpdatePaymentPlanBody,
} from '@ctct/infrastructure-layer';
import { myAccountApi } from '@ctct/infrastructure-layer';

import { AmazonPayCheckoutSessionResponseSchema } from './schemas/amazon-pay.zod';
import { AvailableCurrenciesResponseSchema } from './schemas/available-currencies.zod';
import { BalanceResponseSchema } from './schemas/balance.zod';
import { PaymentPlanResponseSchema } from './schemas/payment-plan.zod';
import { PaymentResponseSchema } from './schemas/payment.zod';
import { CreatePaypalAgreementResponseSchema, CreatePaypalTokenResponseSchema } from './schemas/paypal.zod';
import { Balance } from '../../value-objects';
import { AvailableCurrencies } from '../../value-objects/available-currencies';
import { PaymentPlan } from '../../value-objects/payment-plan';
import { zodService } from '../ZodService';

class PaymentRepo {
  public async createPaypalToken({ returnURL, cancelURL }: { returnURL: string; cancelURL?: string }) {
    const response = await myAccountApi.postCreatePaypalToken({
      returnURL,
      cancelURL: cancelURL ?? returnURL,
    });

    const data = zodService.safeParse(CreatePaypalTokenResponseSchema, response.data);

    return data;
  }

  public async createPaypalAgreement(token: string) {
    const response = await myAccountApi.putPaypalAgreementToken(token);

    const data = zodService.safeParse(CreatePaypalAgreementResponseSchema, response.data);

    return data;
  }

  public async createAmazonPaySignature(body: AmazonPaySignaturePayload) {
    const response = await myAccountApi.postCreateAmazonPaySignature(body);

    const data = zodService.safeParseString(response.data);

    return data;
  }

  public async getAmazonPayCheckoutSession(amazonCheckoutSessionId: string) {
    const response = await myAccountApi.getAmazonPayCheckoutSession(amazonCheckoutSessionId);

    const data = zodService.safeParse(AmazonPayCheckoutSessionResponseSchema, response.data);

    return data;
  }

  public async postPayment(body: PaymentPayload) {
    const response = await myAccountApi.postPayment(body);

    const data = zodService.safeParse(PaymentResponseSchema, response.data);

    return data;
  }

  public async postOverdueBalancePayment(body: OverduePaymentPayload) {
    const response = await myAccountApi.putOverdueBalancePayment(body);

    const data = zodService.safeParse(PaymentResponseSchema, response.data);

    return data;
  }

  public async getAvailableCurrencies() {
    const response = await myAccountApi.getAvailableCurrencies();

    const data = zodService.safeParse(AvailableCurrenciesResponseSchema, response.data);

    const availableCurrencies = new AvailableCurrencies(data);

    return availableCurrencies;
  }

  public async updateCurrency(currencyCode: string) {
    const response = await myAccountApi.postUpdateCurrency(currencyCode);

    const data = zodService.safeParse(PaymentResponseSchema, response.data);

    return data;
  }

  public async getPaymentPlan(isStripe: boolean, isSuspended: boolean, isTrialer = false) {
    const response = await myAccountApi.getPaymentPlan();

    const data = zodService.safeParse(PaymentPlanResponseSchema, response.data);

    const paymentPlan = new PaymentPlan(isStripe, isSuspended, data, isTrialer);

    return paymentPlan;
  }

  public async getBalance() {
    const response = await myAccountApi.getBalance();

    const data = zodService.safeParse(BalanceResponseSchema, response.data);

    const balance = new Balance(data);

    return balance;
  }

  public async updatePaymentPlan(body: UpdatePaymentPlanBody) {
    const response = await myAccountApi.postUpdatePaymentPlan(body);

    const data = zodService.safeParse(PaymentResponseSchema, response.data);

    return data;
  }
}

export const paymentRepo = new PaymentRepo();
